import React, { useMemo, useState } from "react";
import { Share, Plus, Download, X, Smartphone } from "lucide-react";
import usePWAInstall from "@/hooks/usePWAInstall";

const COLLAPSED_KEY = "truth-scan-hub-install-guide-collapsed";

/**
 * Device-aware PWA installation guide with collapsible binary state.
 *
 *   • iOS Safari      → static instructions (Share icon → Add to Home Screen).
 *   • iOS Chrome      → static instructions (Share button → Add to Home Screen).
 *   • Android Chrome  → native install button driven by `beforeinstallprompt`.
 *
 * Clicking the close (X) action does NOT unmount the component. Instead it
 * sets `isCollapsed=true`, which collapses the banner into a tiny floating
 * pill anchored at the bottom-right. Tapping the pill re-expands the full
 * instructions. The component only fully disappears when the app is
 * launched in standalone (installed) PWA mode.
 */
const detectDeviceVariant = () => {
  if (typeof window === "undefined") return "unsupported";
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);

  if (isIOS) {
    if (/CriOS/i.test(ua)) return "ios-chrome";
    return "ios-safari";
  }
  if (isAndroid) return "android";
  return "unsupported";
};

const PWAInstallGuide = () => {
  const { canInstall, installed, isStandalone, promptInstall } =
    usePWAInstall();
  const [variant] = useState(detectDeviceVariant);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const persistCollapsed = (next) => {
    setIsCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      /* storage blocked */
    }
  };

  const collapse = () => persistCollapsed(true);
  const expand = () => persistCollapsed(false);

  const onInstall = async () => {
    const result = await promptInstall();
    if (result && result.outcome === "accepted") collapse();
  };

  // Stays hidden only when running in installed/standalone PWA mode. On
  // every other web client (mobile browser) the component remains
  // mounted in either expanded or collapsed state for the entire session.
  const hidden = useMemo(() => {
    if (isStandalone || installed) return true;
    if (variant === "unsupported") return true;
    // Android: ALWAYS show the explicit "⋮ → Install app" copy.
    // The native install BUTTON inside the banner shows only when we
    // captured the deferred prompt event.
    return false;
  }, [isStandalone, installed, variant]);

  if (hidden) return null;

  // ----- Collapsed: tiny floating pill ---------------------------------
  if (isCollapsed) {
    return (
      <button
        type="button"
        data-testid="pwa-install-pill"
        data-variant={variant}
        aria-label="Show install instructions"
        onClick={expand}
        className="fixed z-50 right-3 bg-[#020617]/95 backdrop-blur-md border border-[#FF0033]/60 text-[#FF0033] hover:bg-[#FF0033] hover:text-white shadow-[0_8px_20px_rgba(0,0,0,0.5)] inline-flex items-center gap-1.5 pl-2.5 pr-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
        style={{ bottom: "max(12px, env(safe-area-inset-bottom))" }}
      >
        <Smartphone size={13} />
        Install
      </button>
    );
  }

  // ----- Expanded: full instruction banner -----------------------------
  return (
    <div
      data-testid="pwa-install-guide"
      data-variant={variant}
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pointer-events-none"
      role="region"
      aria-label="Install Truth Scan Hub"
      style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
    >
      <div className="pointer-events-auto mx-auto max-w-md border border-[#FF0033]/50 bg-[#020617]/95 backdrop-blur-md shadow-[0_-12px_30px_rgba(0,0,0,0.7)]">
        <div className="flex items-start gap-3 p-3.5">
          <div className="shrink-0 w-9 h-9 border border-[#FF0033] flex items-center justify-center">
            {variant === "android" ? (
              <Download size={16} className="text-[#FF0033]" />
            ) : (
              <Share size={16} className="text-[#FF0033]" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF0033] mb-1">
              Install · Truth Scan Hub
            </p>

            {variant === "ios-safari" && (
              <p
                data-testid="install-copy-ios-safari"
                className="text-[13px] leading-snug text-[#cbd5e1]"
              >
                Tap the{" "}
                <span className="inline-flex items-center gap-1 align-middle text-[#f1f5f9] font-bold">
                  <Share size={12} className="text-[#FF0033]" /> Share Icon
                </span>{" "}
                in Safari, then scroll down and tap{" "}
                <span className="inline-flex items-center gap-1 align-middle text-[#f1f5f9] font-bold">
                  &lsquo;Add to Home Screen&rsquo;{" "}
                  <Plus size={12} className="text-[#FF0033]" />
                </span>
                .{" "}
                <span className="block mt-1.5 text-[#94a3b8]">
                  If you cannot see the Share Icon, tap the{" "}
                  <span className="text-[#f1f5f9] font-bold">… (three dots)</span>{" "}
                  menu to find the Share button first.
                </span>
              </p>
            )}

            {variant === "ios-chrome" && (
              <p
                data-testid="install-copy-ios-chrome"
                className="text-[13px] leading-snug text-[#cbd5e1]"
              >
                In Chrome on iOS: tap the{" "}
                <span className="inline-flex items-center gap-1 align-middle text-[#f1f5f9] font-bold">
                  <Share size={12} className="text-[#FF0033]" /> Share
                </span>{" "}
                button in the address bar, scroll down, and tap{" "}
                <span className="text-[#f1f5f9] font-bold">
                  &lsquo;Add to Home Screen&rsquo;
                </span>
                .
              </p>
            )}

            {variant === "android" && (
              <>
                <p className="text-[13px] leading-snug text-[#cbd5e1] mb-2.5">
                  Install CMH Truth Scan for a faster, fullscreen experience.
                </p>
                <p
                  data-testid="install-copy-android"
                  className="text-[12px] leading-snug text-[#94a3b8] mb-2.5"
                >
                  Or tap{" "}
                  <span className="text-[#f1f5f9] font-bold">⋮ (three dots)</span>{" "}
                  in Chrome →{" "}
                  <span className="text-[#f1f5f9] font-bold">Install app</span>{" "}
                  →{" "}
                  <span className="text-[#f1f5f9] font-bold">Install</span>.
                </p>
                <button
                  type="button"
                  data-testid="install-android-button"
                  onClick={onInstall}
                  disabled={!canInstall}
                  className="inline-flex items-center gap-2 bg-[#FF0033] hover:bg-white hover:text-black disabled:bg-[#1e293b] disabled:text-[#475569] disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.16em] text-[11px] px-3 py-2 transition-colors"
                >
                  <Download size={12} /> {canInstall ? "Install CMH Truth Scan" : "Use ⋮ menu above"}
                </button>
              </>
            )}
          </div>

          <button
            type="button"
            data-testid="install-guide-collapse"
            onClick={collapse}
            aria-label="Minimise install instructions"
            className="shrink-0 w-7 h-7 flex items-center justify-center text-[#64748b] hover:text-[#f1f5f9] transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallGuide;
